import { Checkbox, LeaderGroupCombobox, UserCombobox } from '@/components'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { UserOrderBy } from '@/modules/activities'
import { useActivityExportMutations } from '@/modules/activities/mutations.hooks'
import { ExportFormat, ExportOption, ExportType } from '@/modules/exports'
import {
  ExportCard,
  ExportField,
  ExportFormatField,
  ExportMethodField,
  ExportPeriodField,
  getExportPeriodIso,
} from '@/modules/exports/components/export-form'
import { USERS_COMBOBOX_QUERY } from '@/modules/users/queries'
import { notEmptyArrayValidation } from '@/utils/forms'
import { useCompany, useUser } from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import type { ExportCardProps } from '../types'

interface ActivitiesForm {
  month: Date
  dateStart: Date
  dateEnd: Date
  exportOption: ExportOption
  exportFormat: ExportFormat
  useDateRange: boolean
  users: string[]
  weekly: boolean
  leaderGroups: string[]
  detailed: boolean
}

const EXCLUDED_FORMATS = [ExportFormat.EXCEL_FLAT]

interface SalaryUsersFieldProps {
  value: string[]
  error?: string | null
  onChange(value: string[]): void
}

const SalaryUsersField: React.FC<SalaryUsersFieldProps> = ({
  value,
  error,
  onChange,
}) => {
  const translations = useTranslate({
    selectUsers: 'common.select-users',
    selectAll: 'common.select-all',
    clear: 'exports.form.clear-selection',
  })

  const me = useUser()
  const { data } = useQuery(USERS_COMBOBOX_QUERY, {
    variables: {
      includeSuperuser: me.isSuperuser,
      dropdown: DropdownNames.ACTIVITIES,
      filterLeaderGroup: false,
      filterSalaryResponsible: false,
      includeInSalaryCalculation: true,
    },
  })
  const allUserIds = (data?.allUsersList ?? []).map(user => user.id)
  const allSelected =
    allUserIds.length > 0 && allUserIds.every(id => value.includes(id))

  return (
    <ExportField
      label={translations.selectUsers}
      error={error}
      wide
      labelRight={
        <button
          type="button"
          className="text-xs font-semibold text-stone-500 hover:text-stone-900"
          onClick={() => onChange(allSelected ? [] : allUserIds)}
        >
          {allSelected ? translations.clear : translations.selectAll}
        </button>
      }
    >
      <UserCombobox
        mode="multiple"
        value={value}
        dropdownPageName={DropdownNames.ACTIVITIES}
        filterSalaryCalculation
        onChange={onChange}
      />
    </ExportField>
  )
}

export const ExportActivitiesForSalaryCard: React.FC<ExportCardProps> = ({
  loading,
  onLoadingChange: _onLoadingChange,
}) => {
  const translations = useTranslate({
    exportAll: 'settings.export-all-driver-activities',
    detailed: 'common.detailed',
    leaderGroups: 'companies.leader-groups',
    selectUsersError: 'activities.errors.select-multiple-users',
  })

  const me = useUser()
  const company = useCompany()

  const { exportActivitiesMass, loading: exportLoading } =
    useActivityExportMutations()

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    formValid,
    updateForm,
  } = useForm<ActivitiesForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      exportOption: ExportOption.SAVE,
      exportFormat: ExportFormat.PDF,
      useDateRange: false,
      users: [],
      weekly: false,
      leaderGroups: [],
      detailed: false,
    },
    validators: {
      users: (value, other) =>
        notEmptyArrayValidation([...value, ...other.leaderGroups])
          ? translations.selectUsersError
          : null,
      leaderGroups: (value, other) =>
        notEmptyArrayValidation([...value, ...other.users])
          ? translations.selectUsersError
          : null,
    },
    config: {
      initAsInvalid: true,
    },
  })

  function handleExport(values: ActivitiesForm) {
    const [dateStart, dateEnd] = getExportPeriodIso(values)

    void exportActivitiesMass({
      variables: {
        exportType: ExportType.ENTRY_SALARY,
        exportFormat: values.exportFormat,
        user: me.id,
        users: values.users,
        company: company.id,
        dateStart,
        dateEnd,
        includeAbsences: false,
        exportOption: values.exportOption,
        weekly: values.weekly,
        orderBy: UserOrderBy.FAMILY_NAME,
        leaderGroups: values.leaderGroups,
        detailed: values.detailed,
      },
    })
  }

  return (
    <ExportCard
      title={translations.exportAll}
      loading={loading || exportLoading}
      submitDisabled={!formValid}
      onSubmit={submit(handleExport)}
    >
      <ExportPeriodField value={form} onChange={updateForm} />

      <ExportMethodField
        value={form.exportOption}
        onChange={exportOption => updateForm({ exportOption })}
      />

      <ExportFormatField
        value={form.exportFormat}
        exclude={EXCLUDED_FORMATS}
        onChange={exportFormat => updateForm({ exportFormat })}
      />

      <ExportField label={translations.leaderGroups}>
        <LeaderGroupCombobox
          mode="multiple"
          value={form.leaderGroups}
          onChange={leaderGroups => updateForm({ leaderGroups })}
        />
      </ExportField>

      <SalaryUsersField
        value={form.users}
        error={errors.users}
        onChange={users => updateForm({ users })}
      />

      <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-stone-700 md:col-span-2">
        <Checkbox
          size="sm"
          value={form.detailed}
          onChange={checked => updateForm({ detailed: checked === true })}
        />
        {translations.detailed}
      </label>
    </ExportCard>
  )
}
